/*
 * CreatorStorefront — a creator's PUBLIC homepage, viewed by other users from
 * the Community. Read-only: served from /creator/public/* which only exposes
 * published content; no edit affordances exist here by construction.
 */
import React, { useEffect, useRef, useState } from 'react';
import '../../styles/creator.css';
import APIService from '../../services/APIService';
import { renderMarkdown } from './markdown';
import { IconCalendar } from './icons';

const CreatorStorefront = ({ creatorId, person, onBack, onAttempt, backLabel = '← Back to Community' }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    APIService.getEntitlements()
      .then((e) => { if (alive) setSubscribed(!!(e.subs || {})[`creator:${creatorId}`]); })
      .catch(() => {});
    return () => { alive = false; };
  }, [creatorId]);
  const subscribe = async () => {
    setSubBusy(true);
    try { await APIService.subscribeCreator(creatorId); setSubscribed(true); }
    catch { /* surfaced by button state */ } finally { setSubBusy(false); }
  };
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [blog, setBlog] = useState(null);       // opened blog {title, markdown}
  const [packSubs, setPackSubs] = useState({}); // pack_id -> subscribed
  const [packBusy, setPackBusy] = useState(''); // pack id mid-subscribe
  const ivRef = useRef(null);

  useEffect(() => {
    let alive = true;
    APIService.myPackSubscriptions()
      .then((d) => {
        if (!alive) return;
        const map = {};
        (d.packs || []).forEach((p) => { map[p.id] = true; });
        setPackSubs(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [creatorId]);

  const subscribeToPack = async (p) => {
    setPackBusy(p.id);
    try { await APIService.subscribePack(p.id); setPackSubs((m) => ({ ...m, [p.id]: true })); }
    catch { /* surfaced by button state */ } finally { setPackBusy(''); }
  };

  useEffect(() => {
    let alive = true;
    setData(null); setError(''); setBlog(null);
    APIService.creatorPublic(creatorId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError('notfound'); });
    return () => { alive = false; };
  }, [creatorId]);

  const openBlog = async (b) => {
    try { setBlog(await APIService.creatorPublicBlog(creatorId, b.id)); } catch { /* ignore */ }
  };

  const groupsOf = (interviews) => {
    const by = {};
    interviews.forEach((i) => { (by[i.group || 'Interviews'] = by[i.group || 'Interviews'] || []).push(i); });
    return Object.entries(by);
  };

  return (
    <div className="ca-main sf-wrap">
      <button className="ca-ghost" type="button" onClick={onBack}>{backLabel}</button>

      {error ? (
        <div className="ca-center">
          <h1 style={{ color: 'var(--n-text)' }}>{person?.display_name || 'This member'}</h1>
          <p>hasn't opened a creator homepage yet.</p>
        </div>
      ) : !data ? (
        <div className="ca-center">Loading homepage…</div>
      ) : blog ? (
        <div className="ca-panel">
          <button className="ca-ghost" type="button" onClick={() => setBlog(null)}>← All posts</button>
          <h1 className="sf-blog-title">{blog.title}</h1>
          <div className="ca-ed-preview sf-blog">{renderMarkdown(blog.markdown)}</div>
        </div>
      ) : (
        <div className="ca-panel sf-profile">
          {/* ── Hero: the mentor card ── */}
          <div className="sf-hero">
            {person?.avatar
              ? <img className="sf-av big" src={person.avatar} alt="" />
              : <span className="sf-av big ph">{String(person?.display_name || '?').slice(0, 1).toUpperCase()}</span>}
            <div className="sf-hero-main">
              <h1>{person?.display_name || 'Creator'}</h1>
              {(data.current_role || data.company) && (
                <div className="sf-role">
                  {data.current_role}{data.company && <> @ <b>{data.company}</b></>}
                  {data.years_experience > 0 && <span className="sf-yrs"> · {data.years_experience}+ yrs</span>}
                </div>
              )}
              {data.headline && <div className="sf-headline">{data.headline}</div>}
              {(data.domains || []).length > 0 && (
                <div className="sf-chips">{data.domains.map((d) => <span key={d} className="sf-chip acc">{d}</span>)}</div>
              )}
              <div className="sf-links">
                {data.linkedin && <a href={data.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>}
                {data.github && <a href={data.github.startsWith('http') ? data.github : `https://github.com/${data.github}`} target="_blank" rel="noreferrer">GitHub</a>}
                {data.website && <a href={data.website} target="_blank" rel="noreferrer">Website</a>}
              </div>
            </div>
            <div className="sf-hero-side">
              <button className="ca-primary" type="button"
                onClick={() => ivRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                Book a mock interview →
              </button>
              {subscribed && <div className="sf-subbed">✓ Subscribed</div>}
              {data.availability && <div className="sf-avail"><IconCalendar size={13} /> {data.availability}</div>}
            </div>
          </div>

          {(data.mentees > 0 || data.sessions > 0 || data.interviews.length > 0) && (
            <div className="sf-stats">
              {data.mentees > 0 && <div><b>{data.mentees}+</b><span>mentees helped</span></div>}
              {data.sessions > 0 && <div><b>{data.sessions}+</b><span>sessions taken</span></div>}
              <div><b>{data.interviews.length}</b><span>AI interviews</span></div>
              <div><b>{data.blogs.length}</b><span>posts</span></div>
            </div>
          )}

          {data.about_md && (
            <>
              <div className="ca-panel-h sf-sec">About</div>
              <div className="ca-ed-preview sf-about">{renderMarkdown(data.about_md)}</div>
            </>
          )}

          {(data.experience || []).length > 0 && (
            <>
              <div className="ca-panel-h sf-sec">Experience</div>
              <ul className="sf-exp">{data.experience.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </>
          )}

          {(data.skills || []).length > 0 && (
            <>
              <div className="ca-panel-h sf-sec">Skills</div>
              <div className="sf-chips">{data.skills.map((k) => <span key={k} className="sf-chip">{k}</span>)}</div>
            </>
          )}

          <div className="ca-panel-h sf-sec" ref={ivRef}>AI Interviews</div>
          {data.interviews.length === 0 ? <div className="ca-hint">No published interviews yet.</div>
            : groupsOf(data.interviews).map(([group, rows]) => (
              <div key={group} className="sf-group">
                <div className="sf-group-h">{group}</div>
                {rows.map((i) => (
                  <div key={i.id} className="ca-row">
                    <div className="ca-row-main">
                      <span className="ca-row-t">{i.title}</span>
                      <span className="ca-row-m">{i.format === 'structured' ? 'Rubric-graded' : 'Scripted'} · ~{i.duration_min} min · AI voice agent</span>
                    </div>
                    {onAttempt ? (
                      subscribed ? (
                        <button className="ca-primary" type="button" onClick={() => onAttempt(i)}>Attempt →</button>
                      ) : (
                        <button className="ca-ghost" type="button" disabled={subBusy} onClick={subscribe}
                          title="Free during beta — unlocks every interview by this creator">
                          {subBusy ? 'Subscribing…' : 'Subscribe to attempt'}
                        </button>
                      )
                    ) : <span className="sf-soon">Attempt from the Community</span>}
                  </div>
                ))}
              </div>
            ))}

          {(data.packs || []).length > 0 && (
            <>
              <div className="ca-panel-h sf-sec">Study material</div>
              <div className="ca-list">
                {data.packs.map((p) => (
                  <div key={p.id} className="ca-row">
                    <div className="ca-row-main">
                      <span className="ca-row-t">{p.title}</span>
                      <span className="ca-row-m">
                        {p.tagline ? `${p.tagline} · ` : ''}{p.page_count} page{p.page_count === 1 ? '' : 's'} with practice questions
                      </span>
                    </div>
                    {packSubs[p.id] ? (
                      <span className="sf-subbed">✓ In your Library</span>
                    ) : (
                      <button className="ca-primary" type="button" disabled={packBusy === p.id}
                        title="Adds this pack to your Knowledge Hub → Library"
                        onClick={() => subscribeToPack(p)}>
                        {packBusy === p.id ? 'Subscribing…' : 'Subscribe'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="ca-panel-h sf-sec">Blogs</div>
          {data.blogs.length === 0 ? <div className="ca-hint">No posts yet.</div>
            : (
              <div className="ca-list">
                {data.blogs.map((b) => (
                  <button key={b.id} type="button" className="ca-row ca-row-main link" onClick={() => openBlog(b)}>
                    <span className="ca-row-t">{b.title}</span>
                    <span className="ca-row-m">Free · {b.updated_at?.slice(0, 10)}</span>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default CreatorStorefront;
